/* eslint-disable no-mixed-spaces-and-tabs */
import React from 'react';
import { Typography, Stack, useTheme } from '@mui/material';
import { GRID_CHECKBOX_SELECTION_COL_DEF } from '@mui/x-data-grid';
import { formatCurrency, getUpperCaseHeader } from '@/utils/constants';
import PolicyRenewalDate from './PolicyRenewalDate';
import PolicyClientCell from '../../table/DataGridComponents/PolicyClientCell';
import DataGridClientContactName from '../../table/DataGridComponents/DataGridClientContactName';
import PolicyRowCheckbox from '../../table/DataGridComponents/PolicyRowCheckbox';
import { BlinkingCircle } from '@/components/icons/BlinkingCircle';
import { OwnerCellData } from '../OwnerCell';
import { BranchCellData } from '../BranchCell';

const DealStatusCell = ({ hasDeals }) => {
	const theme = useTheme();
	return (
		<Stack direction='row' spacing={1} alignItems={'center'}>
			{hasDeals && <BlinkingCircle fontSize='6px' color={theme.palette.primary.main} />}
			<Typography>{hasDeals ? 'In Progress' : '-'}</Typography>
		</Stack>
	);
};

export const columns = [
	{
		...GRID_CHECKBOX_SELECTION_COL_DEF,
		renderHeader: () => <></>,
		hideable: false,
		headerName: 'Checkbox',
		renderCell: ({ row }) => <PolicyRowCheckbox policy={row} />,
	},
	{
		field: 'ClientName',
		numeric: false,
		headerName: 'Client name',
		editable: false,
		hideable: false,
		sortable: true,
		align: 'left',
		flex: 2,
		minWidth: 200,
		display: 'flex',
		renderCell: ({ colDef, row }) => {
			return <PolicyClientCell {...row} width={colDef.minWidth} />;
		},
	},
	{
		field: 'ClientContact',
		numeric: false,
		headerName: 'Contact name',
		editable: false,
		hideable: true,
		sortable: false,
		align: 'left',
		flex: 1.5,
		minWidth: 160,
		display: 'flex',
		renderCell: ({ row }) => (
			<DataGridClientContactName
				clientId={row.ClientDocId}
				clientContact={row.ClientContact}
			/>
		),
	},
	{
		field: 'PolicyNumber',
		numeric: false,
		headerName: 'Policy number / Class of Risk',
		editable: false,
		hideable: false,
		sortable: false,
		align: 'left',
		flex: 2,
		minWidth: 200,
		display: 'flex',
		renderHeader: (params) => {
			const headers = params.colDef.headerName.split('/');
			return (
				<Stack spacing={1}>
					{headers.map((header, i) => (
						<Typography
							key={`header-${params.field}-${i}`}
							variant='inherit'
							className='MuiDataGrid-columnHeaderTitle'
							component='span'
						>
							{header}
							{i === 0 && ' / '}
						</Typography>
					))}
				</Stack>
			);
		},
		renderCell: ({ row }) => {
			return (
				<Stack spacing={1}>
					<Typography>{row.PolicyNumber}</Typography>
					<Typography>{row.ClassOfRisk}</Typography>
				</Stack>
			);
		},
	},
	{
		field: 'Description',
		numeric: false,
		headerName: 'Description',
		editable: false,
		hideable: true,
		sortable: false,
		align: 'left',
		flex: 2,
		minWidth: 200,
		display: 'flex',
	},
	{
		field: 'BranchId',
		numeric: true,
		headerName: 'Broker Group',
		editable: false,
		hideable: true,
		align: 'left',
		sortable: false,
		flex: 1,
		minWidth: 150,
		display: 'flex',
		renderCell: ({ row }) => {
			if (!row.BranchId) return <></>;
			return <BranchCellData branchId={row.BranchId} color={'text.primary'} />;
		},
	},
	{
		field: 'OwnerDocId',
		numeric: false,
		headerName: 'Client Owner',
		editable: false,
		hideable: true,
		align: 'left',
		sortable: false,
		flex: 1,
		minWidth: 150,
		display: 'flex',
		renderCell: ({ row }) => {
			if (!row.OwnerDocId) return <></>;
			return <OwnerCellData userId={row.OwnerDocId} color={'text.primary'} />;
		},
	},
	{
		// field: 'PolicyToDate',
		field: 'RenewalDate',
		numeric: false,
		headerName: 'Renewal date',
		editable: false,
		hideable: false,
		sortable: true,
		align: 'right',
		flex: 1,
		minWidth: 100,
		display: 'flex',
		renderCell: ({ row }) => {
			return <PolicyRenewalDate {...row} />;
		},
	},
	{
		field: 'status',
		numeric: false,
		headerName: 'Status',
		editable: false,
		hideable: false,
		sortable: false,
		align: 'right',
		flex: 1,
		minWidth: 100,
		display: 'flex',
		renderCell: ({ row }) => {
			const hasDeals = Array.isArray(row.deals) && row.deals.length > 0;
			return <DealStatusCell hasDeals={hasDeals} />;
		},
	},
];

export const CLIENT_PAGE_SIZE = 100;
export const SERVER_PREFETCH_PAGES = 10;

export const FUSE_OPTIONS = {
	keys: ['ClientName', 'Description'],
	threshold: 0.2,
};
